import type { ReactNode } from "react";

export type TableRowActionBuilder<Row extends TableRow = TableRow> = (row: Row) => ReactNode;

/**
 * 최상위 행(TableRow)과 하위 행(TableChildRow)이 공유하는 최소 계약 — 식별·가시성·선택
 * 자격만 진다. 선택 저장소·유틸(canToggleRowSelection/resolveSelectAllState)은 이 타입만
 * 보므로, 하위 행도 최상위 행과 같은 선택 인프라에 그대로 맞물린다.
 */
export abstract class TableRowBase {
  abstract getRowId(): string;

  /**
   * 이 행이 목록에 보이는가. 권한 등 **행 인스턴스 데이터의 순수 함수**여야 한다 —
   * 필터는 source가 snapshot을 만들 때만 실행되므로, 외부 상태(권한 스토어 등)를 클로저로
   * 참조하면 그 상태가 바뀌어도 재평가되지 않는다. 판정값은 행 생성 시점에 행 데이터로
   * 굳혀 넣고, 이후 바뀌면 refresh()/replaceRow로 흘린다(RFC-data-table §canShow 필터링 계약).
   */
  abstract canShow(): boolean;

  /**
   * 이 행이 (지금이 아니라) **본질적으로** 선택 가능한가 — 행 고유 자격만 답한다.
   * 선택 상한 등 행 무관 수용량은 source 소관(DataTableSource.canSelectMore)이고,
   * 뷰·가드는 둘을 합성한 DataTableSource.canSelectRow(row)로 판정한다.
   */
  abstract canSelect(): boolean;
}

export abstract class TableRow extends TableRowBase {
  private readonly rowActionsBuilder: TableRowActionBuilder<TableRow> | null;

  protected constructor(rowActionsBuilder: TableRowActionBuilder<TableRow> | null = null) {
    super();
    this.rowActionsBuilder = rowActionsBuilder;
  }

  get isRowClickable(): boolean {
    return false;
  }

  onRowClick(): void {
    return undefined;
  }

  get hasRowActions(): boolean {
    return this.rowActionsBuilder !== null;
  }

  renderRowActions(): ReactNode {
    return this.rowActionsBuilder?.(this) ?? null;
  }

  /**
   * 하위 행 **선언**(depth 1). DataTable은 renderRowChild가 주어졌고 이 행이 펼쳐졌을 때
   * (isRowChildrenExpanded ?? defaultRowChildrenExpanded) 이 목록 중 canShow()=true인 하위
   * 행을 본 행 바로 아래 그린다.
   *
   * **선언은 접힘/펼침과 무관하다.** 이 목록은 "지금 그릴 것"이 아니라 "이 행이 가진 것"이고,
   * 선택 단위(자식을 선언하면 부모 체크박스는 자식 집계 토글)가 여기서 갈린다 — 접힘에서
   * 빈 배열을 돌려주면 단위가 부모 자신으로 뒤집혀, 접힌 채 담은 선택이 펼치는 순간
   * 고아가 된다(PIT-visibility-selection-identity). 빈 배열은 "하위 행이 아예 없는 행"(그 행 자신이 선택 단위)이라는
   * 뜻으로만 쓴다. 기본은 하위 행 없음.
   */
  getRowChildren(): readonly TableChildRow[] {
    return [];
  }

  /**
   * 하위 행을 펼친 상태인가. **기본 undefined = "이 행은 아직 정하지 않았다"** — 그 행만
   * 표의 `defaultRowChildrenExpanded`(기본 false)가 값을 채운다. 행이 true/false를 돌려주면
   * 표 기본값과 무관하게 그 값이 이긴다.
   *
   * 펼침은 자식의 **가시성(canShow)과 별개 축**이다 — canShow는 권한 숨김처럼 "이 자식이
   * 존재로서 보이는가"만 답하고, 이 값은 "부모가 지금 열려 있는가"를 답한다. 두 축을 한
   * 값에 겹치면 권한으로 숨긴 자식과 접힌 자식을 구분할 수 없다.
   *
   * 상태는 행 데이터가 소유한다 — 토글은 새 행을 흘려서(refresh()/replaceRow) 표현한다
   * (canShow 순수성 계약과 같은 뿌리). 표 기본값은 행이 값을 갖기 전까지의 **초기값**일
   * 뿐이라, 한 번 접은 행은 기본값이 켜져 있어도 접힌 채로 남는다.
   */
  get isRowChildrenExpanded(): boolean | undefined {
    return undefined;
  }

  /**
   * 정렬용 비교. `columnKey` 기준으로 this를 other보다 앞(<0)·같음(0)·뒤(>0)에 둔다.
   * 항상 **오름차순** 기준 값을 반환하고, 내림차순은 DataTable이 부호를 뒤집는다.
   *
   * 기본 구현은 셀 값(this[columnKey] vs other[columnKey])을 숫자/불리언/문자 인지해 비교한다.
   * 파생 필드·날짜·로케일 등 커스텀 순서가 필요하면 행 모델에서 override 한다.
   */
  compare(other: this, columnKey: string): number {
    return compareCellValues(
      (this as Record<string, unknown>)[columnKey],
      (other as Record<string, unknown>)[columnKey],
    );
  }
}

/**
 * 하위 행 — 부모 TableRow.getRowChildren()가 노출하는 depth-1 행. 표 계약(정렬 compare·
 * 행 액션·행 클릭·하위 행 중첩)은 갖지 않고 선택·가시성 계약(TableRowBase)만 진다.
 * 최상위 행과 타입으로 분리돼 source 페이지(onPage 응답)나 최상위 행 전용 API에 섞이면
 * 컴파일 에러로 잡힌다. 내용 렌더는 DataTable renderRowChild 소관이다.
 */
export abstract class TableChildRow extends TableRowBase {
  // TableRow와의 구조적 동형을 깨는 브랜드 — 이 타입 분리가 클래스의 존재 이유다.
  protected readonly tableChildRowBrand = true;

  /**
   * Args:
   *   parentRowId: 이 하위 행을 선언하는 최상위 행의 id. 부모가 자식을 만들 때 자기 id를 넘긴다.
   */
  protected constructor(private readonly parentRowId: string) {
    super();
  }

  /**
   * 이 하위 행을 선언하는 최상위 행의 id. 하위 행은 부모 없이 존재할 수 없으므로 필수 데이터다.
   *
   * **부모 *객체*가 아니라 id 문자열이다.** 행은 불변 값 객체라 부모가 같은 id의 새 인스턴스로
   * 교체돼도(접기/펼치기 등) 이 링크는 그대로 유효하고, 옛 부모 인스턴스를 붙잡지도 않는다.
   * 객체 참조를 들면 자식이 stale한 부모를 가리키는 드리프트가 한 겹 더 생긴다.
   *
   * 소스는 이 값으로 **선택 해제(detach) 판정 자격**만 정한다 — "내 부모가 이번에 커밋된 묶음에
   * 있으니 내 생사를 판단할 수 있다". 실제 생사는 그 묶음의 선언(`TableRow.getRowChildren()`)이
   * 답한다(RFC-data-table §15).
   */
  getParentRowId(): string {
    return this.parentRowId;
  }
}

// 기본 셀 값 비교(오름차순). nullish는 항상 뒤로, 숫자는 수치, 불리언은 false<true,
// 그 외는 문자열로 변환해 자연어 비교(numeric: "ds-2" < "ds-10").
export function compareCellValues(a: unknown, b: unknown): number {
  const aNullish = a === null || a === undefined;
  const bNullish = b === null || b === undefined;
  if (aNullish || bNullish) {
    return aNullish === bNullish ? 0 : aNullish ? 1 : -1;
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  if (typeof a === "boolean" && typeof b === "boolean") {
    return Number(a) - Number(b);
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}
